import { useContext, type ReactNode } from "react"

import { Popover, PopoverContent, PopoverTrigger } from "@coval/ui"

import { InlineFilterPanelContext } from "@/contexts/InlineFilterPanelContext"
import { cn } from "@/utils/cn"

import { useFilterSectionHover } from "./useFilterSectionHover"

interface FilterPopoverProps {
  isOpen: boolean
  onToggle: () => void
  trigger: ReactNode
  children: ReactNode
  width?: "w-72" | "w-80" | "w-96"
  /** Set true when the section should be disabled (e.g. AgentFilterSection) */
  disabled?: boolean
  emptyMessage?: string
  /** Data attributes forwarded to the portaled popover content. */
  contentDataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

/**
 * Filter sections render in the picker's current panel when it provides inline
 * context; other consumers retain their anchored, hover-open flyout.
 */
export const FilterPopover: React.FC<FilterPopoverProps> = ({
  isOpen,
  onToggle,
  trigger,
  children,
  width = "w-80",
  disabled = false,
  emptyMessage = "No options available for this filter.",
  contentDataAttributes,
}) => {
  const inline = useContext(InlineFilterPanelContext)
  const hoverProps = useFilterSectionHover({ isOpen, onToggle, disabled })

  if (inline) {
    return (
      <div {...contentDataAttributes}>
        {disabled ? (
          <p className="px-4 py-6 text-center text-sm text-coval-text-muted">
            {emptyMessage}
          </p>
        ) : (
          children
        )}
      </div>
    )
  }

  return (
    <Popover
      open={disabled ? false : isOpen}
      onOpenChange={(open) => {
        if (!disabled && open !== isOpen) {
          onToggle()
        }
      }}
    >
      <PopoverTrigger asChild>
        <div className={cn(disabled && "pointer-events-none")} {...hoverProps}>
          {trigger}
        </div>
      </PopoverTrigger>
      <PopoverContent
        {...contentDataAttributes}
        side="right"
        align="start"
        sideOffset={8}
        className={cn("p-0", width)}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}
